import { useState } from 'react';
import { ChevronDown, ChevronUp, Building2, Shield, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/shared/SEO';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Build • Protect • Grow Framework',
      icon: null,
      questions: [
        {
          q: 'What is the Build • Protect • Grow framework?',
          a: 'Build • Protect • Grow is our comprehensive approach to supporting trucking professionals at every stage. BUILD helps you launch your authority the right way. PROTECT keeps you compliant and penalty-free. GROW scales your revenue through dedicated dispatch. You can use one, two, or all three services based on your needs.',
        },
        {
          q: 'Do I need to use all three services?',
          a: 'No. Each service stands alone. New carriers often start with Build, then add Protect and Grow as they progress. Established carriers might only need Protect (compliance) or Grow (dispatch). Choose what fits your current stage.',
        },
        {
          q: 'Can I bundle services for a discount?',
          a: 'Yes. When you select multiple services during your application, you\'ll see bundled pricing options. Build packages already include bundled Compliance and Dispatch support for 30, 60, or 90 days depending on your tier.',
        },
      ],
    },
    {
      category: 'Build (Authority Setup)',
      icon: Building2,
      color: '#CD7F32',
      questions: [
        {
          q: 'What is the Build program?',
          a: 'Build is our Authority Launch Operating System—a structured program that guides you through every step of launching your trucking authority. From LLC formation to MC activation, we provide the roadmap, accountability, and support to get you operating legally and profitably.',
        },
        {
          q: 'Do you file my authority for me?',
          a: 'No. Queen B Logistics provides structured guidance and oversight—we tell you exactly what to do and when. You are responsible for completing all government filings. This keeps you in control and ensures you understand your business from day one.',
        },
        {
          q: 'What are the Build tier differences?',
          a: 'Foundation ($997): 30 days compliance + 30 days dispatch support. Accelerator ($1,997): 60 days of each plus additional resources. Elite ($2,997): 90 days of both services plus priority support and advanced business setup guidance.',
        },
        {
          q: 'I already have my authority. Is Build for me?',
          a: 'Build is designed for new authorities. If you already have an active MC, you\'ll benefit more from our Protect (compliance) and Grow (dispatch) services. We\'ll guide you to the right fit during your application.',
        },
        {
          q: 'What\'s the pre-qualification process?',
          a: 'Before selecting Build, you\'ll answer questions about your business status (LLC, MC), financial readiness (startup funds, insurance capability), and commitment to deadlines. This ensures Build is right for you and sets expectations for success.',
        },
      ],
    },
    {
      category: 'Protect (Compliance)',
      icon: Shield,
      color: '#C0C0C0',
      questions: [
        {
          q: 'What does the Protect service include?',
          a: 'Protect is ongoing compliance management: deadline monitoring, renewal alerts, regulatory updates, Drug & Alcohol Consortium coordination, UCR/IFTA tracking, and proactive notifications so you never miss a filing or face penalties.',
        },
        {
          q: 'What are the Protect tier options?',
          a: 'Basic ($99/month): Essential compliance tracking and alerts. Professional ($199/month): Full compliance management with proactive support. Enterprise ($399/month): Premium fleet compliance for multi-truck operations.',
        },
        {
          q: 'Will you handle my compliance filings?',
          a: 'We monitor deadlines and send alerts, but you remain responsible for completing filings. We provide guidance and reminders—you maintain control of your authority.',
        },
        {
          q: 'What happens if I miss a deadline?',
          a: 'Our system is designed to prevent that. You\'ll receive multiple alerts before any deadline. If something is missed despite our notifications, we\'ll help you understand remediation steps, but penalties are your responsibility.',
        },
      ],
    },
    {
      category: 'Grow (Dispatch)',
      icon: TrendingUp,
      color: '#D4AF37',
      questions: [
        {
          q: 'What\'s included in Grow dispatch services?',
          a: 'Dedicated dispatcher, load sourcing and booking, rate negotiation, route planning assistance, broker/shipper communication, and paperwork management. Higher tiers include extended hours and priority support.',
        },
        {
          q: 'What are the Grow tier options?',
          a: 'Bronze ($300/week): Dedicated dispatcher, standard business hours. Silver ($400/week): Extended hours plus rate negotiation focus. Gold ($500/week): Priority access, full optimization, and most comprehensive support.',
        },
        {
          q: 'Do I need my own MC/DOT for dispatch services?',
          a: 'Yes. You must have an active MC and DOT number along with current insurance to use our dispatch services. If you need to get your authority first, start with our Build program.',
        },
        {
          q: 'What equipment types do you dispatch?',
          a: 'We primarily dispatch dry van, reefer, flatbed, and step deck. Contact us directly if you have specialized equipment to discuss whether we can accommodate your operation.',
        },
        {
          q: 'Will I have a dedicated dispatcher?',
          a: 'Yes, all Grow packages include a dedicated dispatcher assigned to your account. They\'ll learn your preferences, preferred lanes, and goals to provide personalized service.',
        },
        {
          q: 'Can I choose which loads to accept?',
          a: 'Absolutely. We present opportunities based on your criteria, but you always have final say. Your truck, your choice.',
        },
      ],
    },
    {
      category: 'Application & Onboarding',
      questions: [
        {
          q: 'How do I apply for services?',
          a: 'Visit our Carrier Onboarding page and select the services you need (Build, Protect, Grow). Complete the application with your company info, authority details, and required documents. You can apply for multiple services at once.',
        },
        {
          q: 'What documents do I need to apply?',
          a: 'Company information, DOT number (or "Applying" if new), MC number if applicable, equipment type, Certificate of Insurance (COI), and W9. Build applicants also complete a pre-qualification assessment.',
        },
        {
          q: 'How fast can I start after applying?',
          a: 'Application review typically takes 1-2 business days. Once approved, Build clients begin their structured program immediately. Protect and Grow services can start within 24-48 hours of approval.',
        },
        {
          q: 'Is there a long-term contract?',
          a: 'No long-term commitments for Protect or Grow services. Build is a one-time program with defined timelines. Ongoing services operate on transparent weekly/monthly billing with 7 days written notice to terminate.',
        },
      ],
    },
    {
      category: 'Payment & Billing',
      questions: [
        {
          q: 'How do payments work?',
          a: 'Build packages are one-time payments at checkout. Protect is billed monthly. Grow is billed weekly. We accept major credit cards and ACH transfers.',
        },
        {
          q: 'Are there setup fees?',
          a: 'No setup fees for any service. You only pay the listed package price. No hidden costs.',
        },
        {
          q: 'Can I upgrade or change my tier?',
          a: 'Yes. You can upgrade or downgrade Protect and Grow services at any time. Changes take effect at the start of the next billing period.',
        },
        {
          q: 'What\'s the refund policy?',
          a: 'If your application is not approved, payments are fully refunded within 5-7 business days. No refunds for partial service periods already rendered.',
        },
      ],
    },
    {
      category: 'The Hive (Client Portal)',
      questions: [
        {
          q: 'What is The Hive?',
          a: 'The Hive is our client portal where you access your Build roadmap, track compliance deadlines, view dispatch activity, upload documents, and communicate with your team. It\'s your central hub for everything Queen B.',
        },
        {
          q: 'How do I access The Hive?',
          a: 'Build clients receive Hive access as part of their package. Protect and Grow clients can subscribe separately or receive access bundled with their service tier.',
        },
        {
          q: 'Is there a mobile app?',
          a: 'The Hive is currently web-based and mobile-responsive, so you can access it from any device. A dedicated mobile app is on our roadmap.',
        },
      ],
    },
  ];

  let globalIndex = 0;

  return (
    <div data-testid="faq-page">
      <SEO 
        title="FAQ | Build • Protect • Grow"
        description="Find answers about Queen B Logistics services: Build (authority setup), Protect (compliance), and Grow (dispatch). Learn about pricing, requirements, and how to get started."
        canonical="https://queenblogistics.com/faq"
        keywords="Queen B Logistics FAQ, trucking dispatch questions, authority setup FAQ, compliance management, owner operator help"
      />
      {/* Page Header */}
      <section className="page-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="limited-badge mb-6">
            Build. Protect. Grow.
          </div>
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase">
            Frequently Asked<br />
            <span className="text-[#D4AF37]">Questions</span>
          </h1>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {faqs.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.category} className="mb-12 last:mb-0">
                <div className="flex items-center gap-3 mb-6">
                  {Icon && (
                    <div 
                      className="w-10 h-10 flex items-center justify-center"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: category.color }} />
                    </div>
                  )}
                  <h2 
                    className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider"
                    style={{ color: category.color || '#D4AF37' }}
                  >
                    {category.category}
                  </h2>
                </div>
                
                <div className="space-y-3">
                  {category.questions.map((faq) => {
                    const currentIndex = globalIndex++;
                    const isOpen = openIndex === currentIndex;
                    
                    return (
                      <div key={currentIndex} className="border border-white/5">
                        <button
                          className="w-full flex items-center justify-between p-5 bg-[#121212] hover:bg-[#1a1a1a] transition-colors text-left"
                          onClick={() => setOpenIndex(isOpen ? null : currentIndex)}
                          data-testid={`faq-trigger-${currentIndex}`}
                        >
                          <span className="font-['Barlow_Condensed'] text-lg font-bold text-white pr-4">
                            {faq.q}
                          </span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-white/50 flex-shrink-0" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <div 
                            className="p-5 bg-[#0a0a0a] border-t border-white/5"
                            data-testid={`faq-content-${currentIndex}`}
                          >
                            <p className="text-white/70 leading-relaxed">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-4">
            Still Have Questions?
          </h2>
          <p className="text-white/60 mb-8">
            Can't find what you're looking for? Our team is here to help you find the right path.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-primary inline-block gold-glow"
              data-testid="faq-contact-cta"
            >
              Contact Us
            </Link>
            <Link
              to="/intake"
              className="btn-secondary inline-block"
              data-testid="faq-apply-cta"
            >
              Start Application
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
